import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { fetchSession } from '#/api/auth';
import { getWithTtl, removeWithTtl, setWithTtl } from '#/lib/expiringStorage';
import { queryClient } from '#/lib/queryClient';
import { LOGIN_FLOW_TTL_MS } from '#/config/auth';

/**
 * 인증 상태 스토어 — 로그인 여부 2단계(guest/member)를 단일 enum 으로 관리.
 *
 * IA(03-정보구조-IA) 권한 노출 모델: 비회원(guest) / 회원(member). 본인 데이터 격리는 백엔드 책임이다.
 * ⚠️ 클라 가드는 UX 레이어일 뿐 — 실제 강제는 백엔드 @PreAuthorize 검증(IA 03:282).
 *
 * 단일 enum(SSoT)을 두고 boolean view 는 getter 로 파생한다(불가능 조합 배제).
 * 권한(role/permission) 디렉티브는 도입하지 않는다 — FO 는 로그인 여부 분기만(MIGRATION_PROMPT §4-2).
 *
 * <p><b>불변식: 세션을 가진 사용자는 회원이다.</b> 가입은 즉시 세션을 발급하고 로그인은 세션을 갱신하므로,
 * "로그인은 됐지만 아직 무언가를 기다리는" 중간 상태가 존재하지 않는다. 이메일 인증 관문(056)이 세 번째 수준
 * (pending)을 요구했으나 기능째 제거되면서 그 수준도 함께 사라졌다 — 되살릴 일이 생기면 백엔드
 * {@code SessionInfo} 에 수준 필드를 추가하는 것이 먼저다(클라이언트에 상태만 늘리지 말 것).
 */
export type AuthLevel = 'guest' | 'member';

/**
 * 로그인 후 복귀지(returnUrl) sessionStorage 키 — 로그인 유도 직전 화면 경로를 보존한다.
 * 로컬 로그인은 `/login?redirect=` 쿼리로 충분하지만, 구글 SSO 는 백엔드 OAuth 왕복(브라우저 전체
 * 리다이렉트)이라 쿼리가 콜백까지 살아남지 못한다. 양 경로가 "복귀지"라는 동일 개념을 한 곳에서 공유하도록
 * sessionStorage 를 단일 매개로 둔다 — 콜백·로그인 화면은 특정 기능(담기 등)을 몰라도 복귀를 처리한다.
 * TTL 봉투로 저장(LOGIN_FLOW_TTL_MS) — 미로그인 이탈 시 잔존을 자동 폐기한다([[expiringStorage]]).
 */
const RETURN_URL_KEY = 'fo.auth.returnUrl';

/**
 * 복귀지 안전성 검증 — open-redirect 방어. 앱 내부 절대경로만 허용한다.
 * sessionStorage 출처라 외부 주입 가능성은 낮으나, returnUrl 은 곧 router 이동 대상이므로 방어적으로 막는다.
 * 허용: '/' 로 시작하는 내부 경로. 차단: 빈 값·절대 URL('http://')·프로토콜 상대('//evil.com') 및 그
 * 백슬래시 변형('/\evil.com' — 브라우저가 '//' 로 정규화해 우회 가능). 두 번째 문자가 슬래시/백슬래시면 차단.
 */
function isSafeReturnUrl(url: string | null): url is string {
  return typeof url === 'string' && url.startsWith('/') && url[1] !== '/' && url[1] !== '\\';
}

export const useAuthStore = defineStore('auth', () => {
  /** 인증 수준(단일 SSoT). 초기값 guest. */
  const authLevel = ref<AuthLevel>('guest');

  /**
   * 로그인 사용자 이메일 — 세션 복원(fetchSession) 시 채워진다. 사용자별 로컬 상태
   * (예: 배너 닫음 localStorage 키 스코프)에 쓰는 표시·식별 보조값이다. 같은 세션에서
   * SPA 로그인 직후에는 null 일 수 있다(다음 부팅 복원 시 채워짐) — 소비처는 폴백을 둔다.
   */
  const email = ref<string | null>(null);

  /**
   * 로그인 후 복귀지(returnUrl) — 보호 동작에서 로그인으로 유도하기 직전, 돌아올 경로를 보존한다.
   * sessionStorage 미러로 SSO 전체 리다이렉트에서도 생존. 기능 비종속(담기·다운로드 등 무엇이든 공용).
   */
  const returnUrl = ref<string | null>(readReturnUrl());

  /** sessionStorage(TTL 봉투)에서 안전한 복귀지를 읽는다(없거나·만료·안전하지 않으면 null). */
  function readReturnUrl(): string | null {
    const raw = getWithTtl<string>(RETURN_URL_KEY, LOGIN_FLOW_TTL_MS);
    return isSafeReturnUrl(raw) ? raw : null;
  }

  /**
   * 복귀지 설정 — 보호 동작에서 로그인으로 유도하기 직전 호출. 안전한 내부 경로만 보존한다.
   * @param url 로그인 완료 후 돌아갈 앱 내부 경로(예: '/materials').
   */
  function setReturnUrl(url: string): void {
    if (!isSafeReturnUrl(url)) return;
    returnUrl.value = url;
    setWithTtl(RETURN_URL_KEY, url);
  }

  /**
   * 복귀지 소비 — 로그인 완료 후 1회 읽고 즉시 비운다(중복 복귀 방지). 로컬·SSO 양 경로가 공용으로 호출한다.
   * @returns 보존된 복귀지, 없으면 null(호출부가 기본 경로 '/' 로 대체).
   */
  function consumeReturnUrl(): string | null {
    const url = returnUrl.value;
    returnUrl.value = null;
    removeWithTtl(RETURN_URL_KEY);
    return url;
  }

  /**
   * 부팅 세션 복원 완료 여부. 메모리 상태는 새로고침마다 guest 로 초기화되므로, 영속 쿠키로 1회 복원한다.
   * 가드가 보호 라우트 판정 전 restoreSession() 의 완료를 보장하는 데 쓴다(중복 호출 방지).
   */
  const sessionRestored = ref(false);
  /** 진행 중 복원 Promise(동시 진입 시 단일 호출 공유). */
  let restorePromise: Promise<void> | null = null;

  /** 로그인 여부 — WorkspaceShell 메뉴 노출·샘플 뷰 분기의 단일 판정. */
  const isAuthenticated = computed(() => authLevel.value !== 'guest');

  /**
   * 로그아웃 → guest. <b>로컬 클라이언트 상태 정리의 단일 지점(SSoT)</b>.
   *
   * <p>헤더 [로그아웃] 버튼과 http.ts 401 인터셉터({@code clearSession})가 모두 이 함수로 수렴하므로,
   * 사용자 전환 시 비워야 할 모든 로컬 상태를 여기서 한 번에 정리한다 — 어느 한 경로만 정리하면
   * 다른 경로에서 이전 사용자 데이터가 새는 누락이 생긴다.
   *  - 인증 수준 → guest, 미완 복귀지 폐기(다음 로그인 흐름 오염 방지).
   *  - vue-query 캐시 전체 무효화 — 사용자별 캐시(분석 결과 등)가 다음 사용자에게 누출되지 않도록.
   *    로컬 로그인은 SPA(리로드 없음)라 캐시가 살아남으므로 명시적 정리가 필수.
   */
  function logout(): void {
    authLevel.value = 'guest';
    email.value = null;
    // 미완 복귀지를 폐기 — 다음 로그인 흐름에 이전 의도가 새지 않도록(보안·정합성).
    consumeReturnUrl();
    // 사용자별 vue-query 캐시(분석 결과 등) 전체 무효화 — 교차계정 누출 차단.
    queryClient.clear();
    // 부팅 복원 플래그를 내려, 다음 보호 라우트 진입 시 가드가 쿠키 세션을 재확인하도록 한다.
    // (미초기화 시 markLoggedIn 이 올린 true 가 남아 restoreSession 이 스킵되고 store↔쿠키 상태가 어긋남)
    sessionRestored.value = false;
  }

  /**
   * 로그인·가입 성공 처리 → member.
   *
   * <p>세션 실체는 백엔드가 발급한 HttpOnly 쿠키(IPSI_SESSION_FO) — 여기서는 클라이언트 상태만 갱신한다.
   * 로그인과 가입이 같은 함수를 부르는 이유는 둘 다 "세션을 받은 직후"라는 같은 사건이기 때문이다.
   */
  function markLoggedIn(): void {
    authLevel.value = 'member';
    // 호출부가 직접 로그인 상태를 확정했으므로 부팅 복원을 다시 돌릴 필요가 없다(중복 me 호출 방지).
    sessionRestored.value = true;
    // 사용자별 로컬 상태 키(배너 닫음 등)의 스코프용 이메일을 채운다 — restoreSession 이 스킵되는
    // 경로(SPA 로그인·가입·SSO 콜백)에서는 여기가 유일한 충전 지점이다. 실패해도 로그인 흐름엔 무영향
    // (소비처가 email 미확정 상태를 스스로 방어한다).
    if (!email.value) {
      void fetchSession().then((session) => {
        if (session?.authenticated) {
          email.value = session.email ?? null;
        }
      });
    }
  }

  /**
   * 세션 만료·401 응답 → 클라이언트 인증 상태를 guest 로 초기화.
   * 쿠키 자체는 백엔드가 관리하므로 프론트는 로컬 상태만 정리한다.
   * http.ts 401 핸들러에서 호출 — 직접 호출 시에도 logout() 과 동일 결과.
   * ⚠️ logout() 이 returnUrl 을 폐기하므로, 401 핸들러는 이 호출 **뒤에** 만료 시점의
   * 현재 화면을 setReturnUrl 로 다시 보존한다(재로그인 후 보던 화면 복귀 — 순서 계약).
   */
  function clearSession(): void {
    logout();
  }

  /**
   * 부팅 시 영속 쿠키(IPSI_SESSION)로 로그인 상태를 복원한다(FR-017·SC-011).
   *
   * 새로고침·탭 재오픈 후 메모리 authLevel 이 guest 로 초기화되므로, 라우터 가드가 보호 라우트를 판정하기 전에
   * 1회 호출해 서버에 세션 유효성을 확인한다. 유효하면 member, 미로그인이면 guest 유지. 한 번 완료되면 이후
   * 호출은 즉시 반환(동시 진입은 단일 Promise 공유).
   */
  async function restoreSession(): Promise<void> {
    if (sessionRestored.value) return;
    if (restorePromise) return restorePromise;
    restorePromise = (async () => {
      const session = await fetchSession();
      if (session?.authenticated) {
        authLevel.value = 'member';
        email.value = session.email ?? null;
      }
      sessionRestored.value = true;
    })();
    try {
      await restorePromise;
    } finally {
      restorePromise = null;
    }
  }

  return {
    authLevel,
    email,
    sessionRestored,
    returnUrl,
    isAuthenticated,
    setReturnUrl,
    consumeReturnUrl,
    logout,
    markLoggedIn,
    clearSession,
    restoreSession,
  };
});
